<template>
    <div>
        <history ref="historyWindow" />
        <status
            ref="statusWindow"
            :refreshAll="false"
            @update-scroll-y="handleScrollY"
        />

        <h1 class="my-3 ml-4 col-sm-9" v-if="_isTvView">
            <img src="assets/daysmartvet_logo_dark.svg" width="37" />
            Census
        </h1>

        <b-row class="my-3">
            <b-col md="7">
                <h1 class="d-sm-inline">
                    <span>
                        {{ headerDate }}
                    </span>
                </h1>
                <actor-name
                    v-if="_timeZone && !_isTvView"
                    :actor="$root.currentActor"
                    :useRouter="false"
                    :text="_timeZone.replace('_', '')"
                />
                <span v-else-if="_isTvView">
                    {{ clinicTimezone }}
                </span>
            </b-col>

            <b-col class="text-right">
                <remote-view-link
                    v-if="!_isTvView"
                    :mod="EXTERNALVIEW.CENSUS"
                />
                <b-button-group class="mr-4">
                    <b-button
                        id="filter-button"
                        @click="openFilter(filterOptions)"
                        class="d-flex align-items-center"
                        :disabled="isLoading"
                        v-b-tooltip.hover.bottom
                        title="Filter census"
                    >
                        <i class="fas fa-filter mr-1"></i>
                        <span
                            v-if="filterSelect.length > 0"
                            class="filter-badge"
                            >{{ filterSelect.length }}</span
                        >
                    </b-button>
                    <b-button
                        @click="refetch(true)"
                        v-b-tooltip.hover.bottom
                        title="Refresh"
                    >
                        <i class="fas fa-sync"></i>
                    </b-button>
                    <div class="btn-group input-group">
                        <b-button
                            class="btn btn-secondary datepickerbutton"
                            type="button"
                            v-b-tooltip.hover.bottom
                            title="Filter by date"
                        >
                            <i class="far fa-calendar"></i>
                        </b-button>
                        <date-picker
                            v-model="currentDayPicker"
                            id="date-time-input"
                            :wrap="true"
                            :config="datePickerOptions"
                            @dp-change="changeDatePicker"
                        ></date-picker>
                    </div>
                    <b-button @click="changeDate(today, true)">
                        Today
                    </b-button>
                </b-button-group>
                <b-button-group class="button-arrow">
                    <b-button
                        class="p-0"
                        @click="changeDate(previousDay, true)"
                        v-b-tooltip.hover.bottom
                        title="Previous day"
                    >
                        <span class="fas fa-chevron-left"></span>
                    </b-button>
                    <b-button
                        class="p-0"
                        @click="changeDate(nextDay, true)"
                        v-b-tooltip.hover.bottom
                        title="Next day"
                    >
                        <span class="fas fa-chevron-right"></span>
                    </b-button>
                </b-button-group>
            </b-col>
        </b-row>
        <b-row no-gutters>
            <b-card class="w-100">
                <div class="list table-responsive">
                    <table class="table table-hover">
                        <thead>
                            <tr>
                                <th>
                                    <sorting
                                        :orderBy="
                                            sortType == 'appointment_status'
                                                ? sort
                                                : null
                                        "
                                        @sorting="
                                            orderBy =>
                                                renderData(
                                                    currentDay,
                                                    'appointment_status',
                                                    orderBy,
                                                    true
                                                )
                                        "
                                        text="Status:"
                                    >
                                    </sorting>
                                </th>
                                <th class="minWidth-header">
                                    <sorting
                                        :orderBy="
                                            sortType == 'triage' ? sort : null
                                        "
                                        @sorting="
                                            orderBy =>
                                                renderData(
                                                    currentDay,
                                                    'triage',
                                                    orderBy,
                                                    true
                                                )
                                        "
                                        text="Triage:"
                                    >
                                    </sorting>
                                </th>
                                <th class="minWidth-header">
                                    <sorting
                                        :orderBy="
                                            sortType == 'time' ? sort : null
                                        "
                                        @sorting="
                                            orderBy =>
                                                renderData(
                                                    currentDay,
                                                    'time',
                                                    orderBy,
                                                    true
                                                )
                                        "
                                        text="Time:"
                                    >
                                    </sorting>
                                </th>
                                <th>Assigned To:</th>
                                <th class="minWidth-header">
                                    <sorting
                                        :orderBy="
                                            sortType == 'provider' ? sort : null
                                        "
                                        @sorting="
                                            orderBy =>
                                                renderData(
                                                    currentDay,
                                                    'provider',
                                                    orderBy,
                                                    true
                                                )
                                        "
                                        text="Provider:"
                                    >
                                    </sorting>
                                </th>
                                <th>Complaint:</th>
                                <th class="minWidth-header">
                                    <sorting
                                        :orderBy="
                                            sortType == 'patient_name'
                                                ? sort
                                                : null
                                        "
                                        @sorting="
                                            orderBy =>
                                                renderData(
                                                    currentDay,
                                                    'patient_name',
                                                    orderBy,
                                                    true
                                                )
                                        "
                                        text="Patient:"
                                    >
                                    </sorting>
                                </th>
                                <th class="minWidth-header">
                                    <sorting
                                        :orderBy="
                                            sortType == 'client_name'
                                                ? sort
                                                : null
                                        "
                                        @sorting="
                                            orderBy =>
                                                renderData(
                                                    currentDay,
                                                    'client_name',
                                                    orderBy,
                                                    true
                                                )
                                        "
                                        text="Client:"
                                    >
                                    </sorting>
                                </th>
                                <th>Medical Note:</th>
                                <th>Documents:</th>
                                <th>Billing:</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody v-if="isLoading">
                            <tr>
                                <td colspan="12">
                                    <loader />
                                </td>
                            </tr>
                        </tbody>
                        <tbody v-else>
                            <tr v-if="appointments.length == 0">
                                <td colspan="12">
                                    <i v-if="filterSelect.length > 0"
                                        >No records found based on filters
                                        applied</i
                                    >
                                    <i v-else>No records available</i>
                                </td>
                            </tr>
                            <template
                                v-else
                                v-for="appointment in appointments"
                            >
                                <tr :key="appointment.appointment_id">
                                    <td class="nowrap">
                                        <a
                                            role="button"
                                            tabindex="0"
                                            class="cursor-pointer"
                                            v-if="
                                                isStatusCanBeEdited(
                                                    appointment
                                                ) &&
                                                !appointment.appointment_status
                                                    .replace(' ', '_')
                                                    .toLowerCase()
                                                    .includes('in_treatment') &&
                                                !_isTvView
                                            "
                                            @click="
                                                openStatusModal(appointment)
                                            "
                                            >{{
                                                appointment.appointment_status
                                            }}</a
                                        >
                                        <b v-else>{{
                                            appointment.appointment_status
                                        }}</b>
                                        <br />
                                        <span>{{
                                            appointment.status_editor
                                        }}</span>
                                        <div
                                            class="text-duration"
                                            v-if="
                                                appointment.appointment_status_duration &&
                                                appointment
                                                    .appointment_status_duration
                                                    .length > 0
                                            "
                                        >
                                            <div>
                                                {{
                                                    calculateTotalTime(
                                                        appointment.appointment_status_duration
                                                    )
                                                }}
                                            </div>
                                            <div>
                                                {{
                                                    calculateStatusTime(
                                                        appointment.appointment_status_duration
                                                    )
                                                }}
                                            </div>
                                        </div>
                                    </td>
                                    <td class="text-nowrap">
                                        <div
                                            v-if="
                                                appointment.triage_id &&
                                                appointment.triage_active == '1'
                                            "
                                        >
                                            <span
                                                :style="
                                                    setTriageStyle(appointment)
                                                "
                                                class="triage-badge"
                                                >{{ appointment.triage }}</span
                                            >
                                            <i
                                                v-if="!_isTvView"
                                                @click="
                                                    openModalTriage(appointment)
                                                "
                                                class="cus-page-white-edit cursor-pointer align-middle ml-2"
                                            />
                                        </div>
                                    </td>
                                    <td class="text-nowrap">
                                        {{ appointment.appointment_time }}
                                    </td>
                                    <td>
                                        {{ appointment.assign_to }}
                                    </td>
                                    <td>
                                        {{
                                            appointment.provider
                                                ? appointment.provider
                                                : ""
                                        }}
                                    </td>
                                    <td>
                                        <b>Appt Type:</b>
                                        {{ appointment.appointment_type_label
                                        }}<br />
                                        <b>Complaint:</b>
                                        {{ appointment.appointment_reason }}
                                    </td>
                                    <td>
                                        <patient-name
                                            :use-router="false"
                                            :patient="
                                                generatePatient(appointment)
                                            "
                                            :show-status="false"
                                        />
                                        <br />
                                        {{ appointment.patient_breed }}
                                    </td>
                                    <td>
                                        <client-name
                                            :use-router="false"
                                            :client="
                                                generateClient(appointment)
                                            "
                                        />
                                        <br />
                                        <span
                                            class="text-nowrap"
                                            v-html="
                                                appointment.client_contact_number
                                            "
                                        ></span>
                                    </td>
                                    <td>
                                        <soap-link
                                            v-if="appointment.soap_url != '#'"
                                            :soap="{
                                                ci_url: appointment.soap_url,
                                                label: appointment.soap_name
                                            }"
                                        ></soap-link>
                                    </td>
                                    <td>
                                        <div
                                            v-for="(webform,
                                            i) in appointment.active_webforms"
                                            :key="i"
                                        >
                                            <WebformLink
                                                v-if="webform"
                                                router-name="client_webform"
                                                :webform="
                                                    generateWebformObject(
                                                        webform
                                                    )
                                                "
                                                :withIcon="true"
                                            />
                                        </div>
                                    </td>
                                    <td>
                                        <span
                                            v-html="
                                                formattedBillingDetail(
                                                    appointment.billing_detail
                                                )
                                            "
                                        ></span>
                                    </td>
                                    <td>
                                        <span
                                            class="cursor-pointer"
                                            style="font-size: 16px;"
                                            @click="
                                                openHistoryModal(appointment)
                                            "
                                        >
                                            <i class="fas fa-book"></i>
                                        </span>
                                    </td>
                                </tr>
                            </template>
                        </tbody>
                    </table>
                </div>
            </b-card>
        </b-row>
        <update-modal-triage
            ref="updateModalTriage"
            :instanceId="censusInstandId"
            @reload="handleReload"
        />
        <filter-census
            ref="filterCensus"
            :target="'filter-button'"
            @filterData="filterData"
        />
    </div>
</template>
<script>
import uuidv4 from "uuid/v4";
import CSETTINGS from "@/constants/settings";
import { DateTime, Interval } from "luxon";
import {
    isEmpty,
    filter,
    includes,
    forEach,
    every,
    uniq,
    map,
    find
} from "lodash";
import ScheduleServices from "@/services/schedule";
import humanizeDuration from "humanize-duration";

const NOT_AVAILABLE = "Not Available";
const CHECKED_IN = 4;
const CHECKED_OUT = 7;

export default {
    components: {
        Loader: () => import("@/components/elements/loader-list"),
        History: () => import("@/module/schedule/appointment/history.vue"),
        Status: () => import("@/module/schedule/appointment/status.vue"),
        ActorName: () => import("@/components/commons/actor-name.vue"),
        PatientName: () => import("@/components/commons/patient-name"),
        ClientName: () => import("@/components/commons/client-name"),
        SoapLink: () => import("@/components/commons/soap-link"),
        RemoteViewLink: () => import("@/components/commons/remote-view-link"),
        WebformLink: () => import("@/components/commons/webform-link"),
        UpdateModalTriage: () =>
            import("@/components/appointments/update-triage-modal"),
        Sorting: () => import("@/module/commons/sorting.vue"),
        FilterCensus: () =>
            import("@/components/appointments/census/filter-census-popover")
    },
    data() {
        return {
            today: null,
            isPickerChangedAuto: false,
            currentDayPicker: moment(),
            currentDay: null,
            previousDay: null,
            nextDay: null,
            originalAppointments: [],
            appointments: [],
            headerDate: "",
            datePickerOptions: {},
            isLoading: true,
            censusInstandId: uuidv4(),
            EXTERNALVIEW: CSETTINGS.EXTERNAL_VIEW,
            clinicTimezone: "",
            filterOptions: {},
            originalFilterOptions: {},
            filterCriteria: {},
            filterSelect: [],
            dateTimeNow: this._now(),
            currentScrollY: null
        };
    },
    created() {
        this.timer = setInterval(() => {
            this.dateTimeNow = this._now();
        }, 60000);
    },
    beforeDestroy() {
        clearInterval(this.timer);
    },
    mounted() {
        let self = this;
        try {
            // Promise wait for both getCensusPreferences and _getSetting to resolve
            Promise.all([self.getCensusPreferences(), self._getSetting()]).then(
                () => {
                    // The self._now() returns DateTime object with the current user's timezone.
                    // So it is better to prevent that because it is personalized to that specific user.
                    let today = self._isTvView ? DateTime.now() : self._now();

                    self.today = today.toFormat(self._getLuxonFormat());
                    self.currentDay = today.toFormat(self._getLuxonFormat());
                    self.previousDay = today
                        .minus({ days: 1 })
                        .toFormat(self._getLuxonFormat());
                    self.nextDay = today
                        .plus({ days: 1 })
                        .toFormat(self._getLuxonFormat());
                    self.datePickerOptions = {
                        allowInputToggle: true,
                        format: self.getDateFormat()
                    };

                    // TODO - EPIC-1000 - to add the soap encoded id in this API
                    self.$store.dispatch("soap/checkSoapEnableBeta");
                    self.renderData(
                        self.currentDay,
                        self.sortType,
                        self.sort,
                        true
                    );
                }
            );
        } catch (error) {
            console.error(error);
        }
    },
    computed: {
        sortType: {
            get() {
                return this.$store.state.censusPreferences.sortingCategory; // prettier-ignore
            },
            set(value) {
                this.$store.commit(
                    "censusPreferences/setCensusSortingCategory",
                    value
                );
            }
        },
        sort: {
            get() {
                return this.$store.state.censusPreferences.sortingSortBy; // prettier-ignore
            },
            set(value) {
                this.$store.commit(
                    "censusPreferences/setCensusSortingSortBy",
                    value
                );
            }
        }
    },
    methods: {
        handleReload(reloadPayload) {
            let self = this;
            if (self.currentScrollY || reloadPayload.localScrollY) {
                window.scrollTo(
                    0,
                    parseInt(
                        self.currentScrollY
                            ? self.currentScrollY
                            : reloadPayload.localScrollY,
                        10
                    )
                );
                self.currentScrollY = null;
            }
            self.refetch();
        },
        /**
         * VET-8478
         * @method renderData
         * @description
         * variable fullReload is used for checking if the census needs loading state
         * this enhancement were implemented to prevent full reload on every socket triggered
         */
        async renderData(day, orderBy, order, fullReload) {
            let self = this;
            self.currentDay = day;
            self.currentDayPicker = moment(day, self.getDateFormat());
            self.isPickerChangedAuto = true;
            if (fullReload) {
                self.isLoading = true;
            }

            this.sortType = orderBy;
            this.sort = order;

            let params = {
                timestamp: this._fromDateStrToDateTime(day, true),
                orderCategory: orderBy,
                orderBy: order
            };

            if (this._isTvView) {
                params.token = this.$route.params.token;
                params.isRemoteView = 1;
            } else {
                /* Ensure prefrences were set on change only for non-remote view*/
                this.$store.dispatch("censusPreferences/updateCensusPref");
            }

            return await ScheduleServices.getCensusList(params)
                .then(function (res) {
                    if (res.code == 200) {
                        const resResource = res.resources;
                        self.headerDate = resResource.header.header_date;
                        self.nextDay = resResource.header.next_timestamp;
                        self.previousDay = resResource.header.prev_timestamp;

                        // Save the original data
                        self.originalAppointments = resResource.data
                            ? resResource.data
                            : [];
                        // Initialize appointments with the original data
                        self.appointments = [...self.originalAppointments];

                        self.originalFilterOptions = resResource.filter_option
                            ? resResource.filter_option
                            : {};

                        self.filterOptions = self.originalFilterOptions;

                        if (self.filterSelect.length > 0) {
                            self.filterData(self.filterSelect);
                        }

                        if (self._isTvView) {
                            self.clinicTimezone = resResource?.timezone;
                        }
                    } else {
                        self._message(
                            "Error: Unable to get the data",
                            "danger"
                        );
                    }

                    if (fullReload) {
                        self.isLoading = false;
                    }
                })
                .finally(function () {
                    self.isPickerChangedAuto = false;
                });
        },
        changeDate(day, fullReload) {
            this.renderData(day, this.sortType, this.sort, fullReload);
        },
        /**
         * @method refetch
         * @description
         * when change status, this function will triggered & also use to reload data
         */
        refetch(fullReload = false) {
            this.renderData(
                this.currentDay,
                this.sortType,
                this.sort,
                fullReload
            );
        },
        changeDatePicker(event) {
            let currentDaySelected = event.date.format(this.getDateFormat());
            if (!this.isPickerChangedAuto) {
                // only trigger when user change manually not when model change by code
                this.renderData(
                    currentDaySelected,
                    this.sortType,
                    this.sort,
                    true
                );
            }
        },
        openDatePicker() {
            this.$refs.datepickercensus.show();
        },
        openStatusModal(appointment) {
            this.$refs.statusWindow.open({
                id: appointment.appointment_id
            });
        },
        openHistoryModal(appointment) {
            this.$refs.historyWindow.open({ id: appointment.appointment_id });
        },
        generatePatient(appointment) {
            return {
                name: appointment.patient_name,
                ci_url: appointment.patient_url
            };
        },
        generateClient(appointment) {
            return {
                name: appointment.client_name,
                ci_url: appointment.client_name.includes("(deleted)")
                    ? ""
                    : appointment.client_url
            };
        },
        isStatusCanBeEdited(appointment) {
            return (
                (appointment.appointment_link.includes(
                    "census_model.show_edit_dialog"
                ) ||
                    appointment.appointment_link
                        .toLowerCase()
                        .includes("cancelled")) &&
                !appointment.isPatientInTreatmentBoard
            );
        },
        generateWebformObject(webform) {
            return {
                id: webform.id,
                encodedId: webform.encoded_id,
                title: webform.title,
                submitted_at: webform.submitted_at,
                is_sent: webform.is_sent,
                sent_date: webform.sent_date
            };
        },
        openModalTriage(appointment) {
            this.$refs.updateModalTriage.open(appointment);
        },
        setTriageStyle(appointment) {
            return {
                backgroundColor: appointment.triage_background_color
                    ? `#${appointment.triage_background_color}`
                    : "",
                color: appointment.triage_text_color
                    ? `#${appointment.triage_text_color}`
                    : "",
                borderColor: appointment.triage_border_color
                    ? `#${appointment.triage_border_color}`
                    : ""
            };
        },
        openFilter(filterOptions) {
            this.$refs.filterCensus.show(filterOptions);
        },
        filterData(data) {
            let self = this;
            self.isLoading = true;
            self.filterSelect = data;
            self.filterCriteria = {};

            forEach(self.filterSelect, item => {
                const separatorIndex = item.indexOf("-");
                const key = item.substring(0, separatorIndex);
                const value = item.substring(separatorIndex + 1);

                if (!self.filterCriteria[key]) {
                    self.filterCriteria[key] = [];
                }

                if (value === NOT_AVAILABLE) {
                    if (key === "triage") {
                        self.filterCriteria[key].push(null);
                    } else if (key === "provider") {
                        self.filterCriteria[key].push(false);
                    }
                } else {
                    self.filterCriteria[key].push(value);
                }
            });

            /* Check if has criteria and filter selected, filter the data
            else use the original data without filter */
            if (!isEmpty(self.filterCriteria) && self.filterSelect.length > 0) {
                let appointmentData = filter(
                    self.originalAppointments,
                    appointment => {
                        return every(self.filterCriteria, (values, key) => {
                            if (key === "triage" && includes(values, null)) {
                                return (
                                    includes(values, appointment[key]) ||
                                    (includes(values, null) &&
                                        appointment[key] === null)
                                );
                            }
                            if (key === "provider" && includes(values, false)) {
                                return (
                                    includes(values, appointment[key]) ||
                                    (includes(values, false) &&
                                        appointment[key] === false)
                                );
                            }
                            return includes(values, appointment[key]);
                        });
                    }
                );

                if (appointmentData.length > 0) {
                    self.appointments = appointmentData;
                } else {
                    self.appointments = [];
                }
            } else {
                self.appointments = self.originalAppointments;
            }

            self.renderNewFilterOptions();
            self.isLoading = false;
        },
        renderNewFilterOptions() {
            if (this.filterSelect.length == 0) {
                this.filterOptions = this.originalFilterOptions;
            } else {
                this.filterOptions = this.generateFilterOptionsFromFilteredData(
                    this.appointments
                );
            }

            this.$refs.filterCensus.renderOptions(this.filterOptions);
        },
        generateFilterOptionsFromFilteredData(data) {
            let triageSet = [];
            let assignToSet = [];
            let providerSet = [];
            let appointmentStatusSet = [];

            forEach(data, item => {
                triageSet.push(
                    item.triage !== null ? item.triage : NOT_AVAILABLE
                );
                assignToSet.push(item.assign_to);
                providerSet.push(
                    item.provider !== false ? item.provider : NOT_AVAILABLE
                );
                appointmentStatusSet.push(item.appointment_status);
            });

            let filterCategory = this.getFilterCategory(this.filterSelect);

            return {
                appointment_status:
                    filterCategory === "appointment_status"
                        ? this.originalFilterOptions.appointment_status
                        : this.convertArrayToObject(
                              uniq(appointmentStatusSet),
                              this.originalFilterOptions.appointment_status
                          ),
                triage:
                    filterCategory === "triage"
                        ? this.originalFilterOptions.triage
                        : this.convertArrayToObject(
                              uniq(triageSet),
                              this.originalFilterOptions.triage
                          ),
                assign_to:
                    filterCategory === "assign_to"
                        ? this.originalFilterOptions.assign_to
                        : this.convertArrayToObject(
                              uniq(assignToSet),
                              this.originalFilterOptions.assign_to
                          ),
                provider:
                    filterCategory === "provider"
                        ? this.originalFilterOptions.provider
                        : this.convertArrayToObject(
                              uniq(providerSet),
                              this.originalFilterOptions.provider
                          )
            };
        },
        getFilterCategory(selectedOpt) {
            const categories = selectedOpt.map(option => option.split("-")[0]);
            const allSame = categories.every(
                category => category === categories[0]
            );
            // Return the common category or null
            return allSame ? categories[0] : null;
        },
        convertArrayToObject(array, referenceObject) {
            let obj = {};

            map(array, value => {
                let matched = false;

                forEach(referenceObject, (refValue, key) => {
                    if (
                        refValue === value ||
                        (value === NOT_AVAILABLE && key == "-1")
                    ) {
                        obj[key] = value;
                        matched = true;
                    }
                });
            });

            return obj;
        },
        formattedBillingDetail(billing_detail) {
            if (Array.isArray(billing_detail)) {
                return map(billing_detail, detail => {
                    let ci_link = process.env.APPS + detail.ci_url;
                    return `<a href="${ci_link}">${detail.label}</a>`;
                }).join("<br>");
            }
            return "";
        },
        async getCensusPreferences() {
            if (!this._isTvView) {
                await this.$store.dispatch(
                    "censusPreferences/initGetCensusPrefs"
                );
            }
        },
        calculateTotalTime(duration) {
            // Filter out null or undefined entries
            const filteredDuration = duration.filter(
                item => item !== null && item.status_id
            );

            const statusCheckedIn = find(
                filteredDuration,
                item => item.status_id === CHECKED_IN
            );

            if (!statusCheckedIn) {
                return;
            }

            let startTime = DateTime.fromSeconds(
                Number(statusCheckedIn.start_time)
            );

            const lastStatus = find(
                filteredDuration,
                item => item.status_id !== CHECKED_IN && item.end_time !== null
            );

            let endTime = this.dateTimeNow;
            if (lastStatus) {
                endTime = DateTime.fromSeconds(Number(lastStatus.end_time));
            }

            let intervalInMs = Interval.fromDateTimes(startTime, endTime)
                .toDuration()
                .valueOf();

            if (
                lastStatus &&
                lastStatus.status_id === CHECKED_OUT &&
                lastStatus.total_duration
            ) {
                intervalInMs = lastStatus.total_duration * 1000;
            }

            const humanizedDuration = humanizeDuration(intervalInMs, {
                delimiter: " ",
                units: ["d", "h", "m", "s"],
                round: true
            });

            return `Total Time: ${humanizedDuration}`;
        },
        calculateStatusTime(duration) {
            // Filter out null or undefined entries
            const filteredDuration = duration.filter(
                item => item !== null && item.status_id
            );

            const statusCheckedIn = find(
                filteredDuration,
                item => item.status_id === CHECKED_IN
            );

            if (!statusCheckedIn) {
                return;
            }

            let startTime = DateTime.fromSeconds(
                Number(statusCheckedIn.start_time)
            );

            const nextStatus = find(
                filteredDuration,
                item => item.status_id !== CHECKED_IN
            );

            let endTime = this.dateTimeNow;
            if (statusCheckedIn.end_time) {
                if (nextStatus && nextStatus.start_time) {
                    startTime = DateTime.fromSeconds(
                        Number(nextStatus.start_time)
                    );
                }

                if (nextStatus && nextStatus.end_time) {
                    // Do not display Status Time for Checked Out
                    return;
                }
            }

            const intervalInMs = Interval.fromDateTimes(startTime, endTime)
                .toDuration()
                .valueOf();

            const humanizedDuration = humanizeDuration(intervalInMs, {
                delimiter: " ",
                units: ["d", "h", "m", "s"],
                round: true
            });

            return `Status Time: ${humanizedDuration}`;
        },
        handleScrollY(value) {
            this.currentScrollY = value;
        }
    },
    sockets: {
        "v-broadcast-all": function (event) {
            // Any appointment update will trigger here
            if (
                event.action == "census-refresh" &&
                event.instance != this.censusInstandId &&
                event.businessId == this.$root.setting.business_id &&
                event.params &&
                this.currentDay ==
                    this._setDateDisplay(event.params.appointmentDate)
            ) {
                this.currentScrollY =
                    this.currentScrollY > 0
                        ? this.currentScrollY
                        : window.scrollY;
                this.refetch();
            }
        }
    }
};
</script>
<style scoped type="text/css">
.button-arrow > button {
    font-size: 1em;
    padding: 0.23rem;
    padding-top: 0.18rem;
    width: 2.1rem;
    height: 2rem;
}

input#date-time-input {
    visibility: hidden;
    position: absolute;
    top: 0;
}

th,
td {
    border-top: 0 !important;
    border-bottom: 1px solid #dee2e6 !important;
    vertical-align: baseline !important;
}

.minWidth-header {
    min-width: 100px;
}

.nowrap {
    white-space: nowrap;
}

.text-duration {
    font-size: 13px;
    color: gray;
}
</style>
